import {faDownload} from "@fortawesome/pro-solid-svg-icons/faDownload";
import {generateStatementPdf, GetItemCounts, useInfiniteClientStatements} from "@twigsby/client-data";
import {
    Button,
    ContentBlock,
    ContentLoader,
    FlexAlign,
    FlexJustify,
    InfiniteLoadTrigger,
    ItemRow, ItemRowHeader,
    ItemRowSize,
    StackHorizontal,
    useTranslation
} from "@twigsby/frontend-framework";
import {FormatLocaleDateTimeMonthYear} from "@twigsby/helpers";
import React, {useEffect, useRef, useState} from "react";
import {useSite} from "../../../Providers/SiteInterfaceProvider";

type StatementType = {
    clientId: number;
    month: Date;
    emailStatementSent?: any;
    lettersForwarded: number;
    lettersReceived: number;
    lettersSent: number;
    mailCountId: number;
    pagesScanned: number;
    parcelsForwarded: number;
    parcelsReceived: number;
}

const StatementEntry = (props: StatementType) => {
    const { t } = useTranslation();
    const { currentLocale } = useSite();

    const generatePdf = async (date: Date) => {
        //TODO: move to generic file parser/downloader
        const parsedDate = date.toString().split('T')[0];

        const r: any = await generateStatementPdf(parsedDate);
        const newPdf= new File([r.data], parsedDate, {type: "application/pdf"});

        const windowUrl = window.URL || window.webkitURL;
        const href = windowUrl.createObjectURL(newPdf);
        const a = document.createElement('a');

        a.setAttribute('download', parsedDate);
        a.setAttribute('href', href);
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
    }

    const formatMonth = (input: any) => {
        return FormatLocaleDateTimeMonthYear(new Date(input), currentLocale);
    };

    return (
        <ItemRow
            static={true}
        >
            <div>
                {formatMonth(props.month)}
            </div>
            <div>
                {props.lettersReceived}
            </div>
            <div>
                {props.pagesScanned}
            </div>
            <div>
                {props.lettersForwarded}
            </div>
            <div>
                {props.lettersSent}
            </div>
            <div>
                {props.parcelsReceived}
            </div>
            <div>
                {props.parcelsForwarded}
            </div>
            <div className={'text-right'}>
                <Button
                    onClick={() => generatePdf(props.month)}
                    title={t('actions.download')}
                    icon={faDownload}
                    iconOnly={true}
                    squared={true}
                />
            </div>
        </ItemRow>
    )
}

const Statements = () => {
    const { t } = useTranslation();
    const { currentLocale, updatePageMeta } = useSite();

    const {
        data,
        status,
        error,
        isFetchingNextPage,
        fetchNextPage,
        hasNextPage
    } = useInfiniteClientStatements();

    const [itemCounts, setItemCounts] = useState<any>({ loaded: 0, total: 0 } );

    const wrapperRef = useRef<any>();

    useEffect(() => {
        if(data) {
            setItemCounts(GetItemCounts(data));
        }
    }, [data]);

    useEffect(() => {
        updatePageMeta({
            title: t('settings.statements.title')
        });
    }, [currentLocale]);

    return (
        <>
            <StackHorizontal align={FlexAlign.Center} justify={FlexJustify.Between} extraClass={'mb-xs'}>
                <h2 className={'mb-0'}>{t('settings.statements.title')}</h2>
            </StackHorizontal>

            <div className={'card statements-rows'}>
                <ItemRowHeader sticky={true}>
                    <div>
                        {t('settings.statements.headings.month')}
                    </div>
                    <div>
                        {t('settings.statements.headings.lettersReceived')}
                    </div>
                    <div>
                        {t('settings.statements.headings.pagesScanned')}
                    </div>
                    <div>
                        {t('settings.statements.headings.lettersForwarded')}
                    </div>
                    <div>
                        {t('settings.statements.headings.lettersSent')}
                    </div>
                    <div>
                        {t('settings.statements.headings.parcelsReceived')}
                    </div>
                    <div>
                        {t('settings.statements.headings.parcelsForwarded')}
                    </div>
                    <div />
                </ItemRowHeader>

                <div  ref={wrapperRef}>
                    {status === "loading" ? (
                        <ContentLoader childHeight={'5em'}/>
                    ) : status === "error" ? (
                        <ContentBlock error={true} title={error.name} content={error.message} />
                    ) : (
                        <>
                            {data.pages.map((group) =>
                                group.data.map((item, index) => {
                                    return <StatementEntry key={`transactionItem${index}`} {...item} />
                                })
                            )}
                        </>
                    )}
                </div>

                <InfiniteLoadTrigger
                    loaded={itemCounts.loaded}
                    totalCount={itemCounts.total}
                    hasNextPage={hasNextPage}
                    fetchNextPage={fetchNextPage}
                    isFetchingNextPage={isFetchingNextPage}
                />
            </div>
        </>
    )
}

export default Statements
